/**
 * ThemeProvider Component
 *
 * Root component that provides theme context to all children.
 * Supports either a static theme or a ColorMode controller.
 *
 * The supported standalone default is scope-attribute sync first
 * (`data-smth-theme` / `data-smth-brand`). CSS variable writes remain an
 * explicit runtime bridge.
 *
 * @module @smoothie-framework/ui/theme
 */
import { DEFAULT_THEME_CSS_PREFIX, getTheme, setTheme, setThemeCssPrefix } from './theme_context';
import { applyThemeCssVariables, applyThemeScopeAttributes } from './css_vars';
import { createNodeId } from '../render/node-id';
function shouldWarnThemeProviderMisuse() {
    if (typeof globalThis !== 'object' || globalThis === null || !('process' in globalThis)) {
        return true;
    }
    const runtimeProcess = globalThis.process;
    return runtimeProcess?.env?.NODE_ENV !== 'production';
}
function warnThemeProviderMisuse(message) {
    if (shouldWarnThemeProviderMisuse()) {
        globalThis.console?.warn(`[smoothie/ui] ${message}`);
    }
}
function reportThemeProviderMisuse(props) {
    const { theme, colorMode, themeMode, brand, applyCssVariables, cssPrefix, cssTarget, } = props;
    if (colorMode) {
        if (theme !== undefined) {
            warnThemeProviderMisuse('`theme` is ignored when `colorMode` is provided. Configure themes on the color-mode controller instead.');
        }
        if (themeMode !== undefined) {
            warnThemeProviderMisuse('`themeMode` is ignored when `colorMode` is provided. Runtime mode is controlled by the color-mode controller.');
        }
        if (brand !== undefined) {
            warnThemeProviderMisuse('`brand` is ignored when `colorMode` is provided. Use `createColorMode({ brand })` or `colorMode.syncNow({ brand })` instead.');
        }
        if (applyCssVariables !== undefined) {
            warnThemeProviderMisuse('`applyCssVariables` is ignored on ThemeProvider when `colorMode` is provided. Use `createColorMode({ applyCssVariables })` or `colorMode.syncNow({ applyCssVariables })` instead.');
        }
        if (cssPrefix !== undefined) {
            warnThemeProviderMisuse('`cssPrefix` is ignored on ThemeProvider when `colorMode` is provided. Use `createColorMode({ cssPrefix })` or `colorMode.syncNow({ cssPrefix })` instead.');
        }
        if (cssTarget !== undefined) {
            warnThemeProviderMisuse('`cssTarget` is ignored on ThemeProvider when `colorMode` is provided. Use `createColorMode({ cssTarget })` or `colorMode.syncNow({ cssTarget })` instead.');
        }
        return;
    }
    if (theme !== undefined && themeMode === undefined && applyCssVariables !== true) {
        warnThemeProviderMisuse('Static `ThemeProvider theme={...}` now defaults to selector-first sync. Pass `themeMode` for compiled-scope theming or `applyCssVariables` to opt into the runtime CSS-variable bridge.');
    }
}
/**
 * ThemeProvider component
 *
 * Provides theme context to all child components. By default it syncs selector
 * scope attributes to the DOM and leaves CSS variable writes as an explicit
 * opt-in bridge.
 *
 * @example
 * ```tsx
 * import { ThemeProvider, createColorMode } from '@smoothie-framework/ui';
 *
 * const colorMode = createColorMode({
 *   initialPreference: 'system',
 *   followSystem: true,
 *   persistKey: 'smth-color-mode',
 * });
 *
 * function App() {
 *   return (
 *     <ThemeProvider colorMode={colorMode}>
 *       <Stack>
 *         <Button variant="primary">Themed Button</Button>
 *       </Stack>
 *     </ThemeProvider>
 *   );
 * }
 * ```
 */
export function ThemeProvider(props) {
    reportThemeProviderMisuse(props);
    const { theme, colorMode, children, themeMode, brand, applyCssVariables = false, cssPrefix = '--smth', cssTarget, } = props;
    if (colorMode) {
        // Color mode controller is the single runtime sync authority.
        // It updates theme context + scope attributes reactively from its own effects.
        // ThemeProvider only establishes the subtree boundary in this mode.
    }
    else {
        const resolvedTheme = theme ?? getTheme();
        setTheme(resolvedTheme);
        applyThemeScopeAttributes({
            target: cssTarget,
            mode: themeMode,
            brand,
        });
        if (applyCssVariables) {
            setThemeCssPrefix(cssPrefix);
            applyThemeCssVariables(resolvedTheme, {
                prefix: cssPrefix,
                target: cssTarget,
                includeTextStyles: themeMode === undefined && brand === undefined,
            });
        }
        else {
            setThemeCssPrefix(DEFAULT_THEME_CSS_PREFIX);
        }
    }
    // Normalize children to array
    const childNodes = [];
    if (Array.isArray(children)) {
        childNodes.push(...children);
    }
    else if (children) {
        childNodes.push(children);
    }
    // Return a Fragment-style node with children only.
    return {
        id: createNodeId(),
        type: 'Fragment',
        props: {},
        children: childNodes,
        ...(colorMode ? {} : { _theme: theme ?? getTheme() }),
    };
}
//# sourceMappingURL=ThemeProvider.js.map